import React, { useState } from 'react';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'プロジェクト作成',
    title: 'プロジェクト作成 + Steeringルール設置',
    path: '/devdocs/handson/project-setup',
  },
  {
    label: 'API仕様書取込',
    title: 'API仕様書の取り込み',
    path: '/devdocs/handson/api-specs',
  },
  {
    label: '画面入出力定義書',
    title: '画面入出力定義書の作成',
    path: '/devdocs/handson/screen-io',
  },
  { label: '画面実装', title: '画面実装', active: true },
];

const DESIGN_PATTERNS = [
  {
    id: 'x',
    label: 'X（旧Twitter）風',
    description:
      'ダークモード基調。濃紺の背景に白文字、ブルーのアクセント。情報密度が高く、カードをボーダーで区切るスタイル',
    color: '#1D9BF0',
    bgcolor: '#15202B',
  },
  {
    id: 'facebook',
    label: 'Facebook風',
    description:
      '白とグレーの明るいトーン。薄グレー背景の上にシャドウ付き白カードを並べるレイアウト。ブルーのアクションボタン',
    color: '#1877F2',
    bgcolor: '#F0F2F5',
  },
  {
    id: 'notion',
    label: 'Notion風',
    description: '純白ミニマル。装飾を極限まで排除し、フォントサイズと余白だけで情報の階層を表現',
    color: '#37352F',
    bgcolor: '#FFFFFF',
  },
  {
    id: 'slack',
    label: 'Slack風',
    description:
      '左にダークパープルのサイドバー、右に白いメインエリア。カラフルなバッジやアイコンでステータスを表現',
    color: '#611F69',
    bgcolor: '#3F0E40',
  },
  {
    id: 'github',
    label: 'GitHub風',
    description:
      '白基調に落ち着いたグレートーン。濃色ヘッダー + タブナビゲーション。テーブルとボーダーで情報を整然と並べる',
    color: '#0969DA',
    bgcolor: '#F6F8FA',
  },
];

const PATTERN_CODES: Record<string, string> = {
  x: `---
inclusion: fileMatch
globs:
  - "src/**/*.ts"
  - "src/**/*.tsx"
---

# デザインガイドライン

## デザインの方向性

X（旧Twitter）のようなダークモードを基調としたモダンなデザイン。
情報密度を高く保ちつつ、重要な要素はアクセントカラーで視認性を確保する。

## カラーテーマ

| 用途 | 色 |
|------|-----|
| 背景色（メイン） | #15202B |
| 背景色（カード） | #1E2D3D |
| メインカラー | #1D9BF0 |
| アクセントカラー | #1D9BF0 |
| テキスト色（主） | #E7E9EA |
| テキスト色（副） | #8B98A5 |
| ボーダー色 | #38444D |
| エラー色 | #F4212E |
| 成功色 | #00BA7C |

## ナビゲーション構造

- ヘッダー: アプリ名を左寄せ表示。背景は #15202B で固定
- サイドバー: なし
- メインエリア: 中央寄せ（max-width: 600px）で情報を表示
- 画面遷移: React Routerで切り替え

## レスポンシブ対応

PC表示のみを対象とする。モバイル対応は行わない。

## その他の方針

- MUIコンポーネントを使用し、テーマをダークモードに設定する
- アイコンは @mui/icons-material から選定する
- カード形式でデータを表示し、ボーダーで区切る
- ホバー時に背景色を微妙に明るくするインタラクションを加える
- 角丸は 16px を基本とする`,
  facebook: `---
inclusion: fileMatch
globs:
  - "src/**/*.ts"
  - "src/**/*.tsx"
---

# デザインガイドライン

## デザインの方向性

Facebookのような白基調のクリーンなデザイン。
カード形式で情報を整理し、青をアクセントカラーとして使用する。
情報量が多くても読みやすいレイアウトを重視する。

## カラーテーマ

| 用途 | 色 |
|------|-----|
| 背景色（メイン） | #F0F2F5 |
| 背景色（カード） | #FFFFFF |
| メインカラー | #1877F2 |
| アクセントカラー | #42B72A |
| テキスト色（主） | #050505 |
| テキスト色（副） | #65676B |
| ボーダー色 | #DADDE1 |
| エラー色 | #FA3E3E |
| 成功色 | #42B72A |

## ナビゲーション構造

- ヘッダー: 白背景にアプリ名と主要ナビゲーションを配置。下部にシャドウ
- サイドバー: なし
- メインエリア: 中央寄せ（max-width: 680px）、グレー背景の上にカードを配置
- 画面遷移: React Routerで切り替え

## レスポンシブ対応

PC表示のみを対象とする。モバイル対応は行わない。

## その他の方針

- MUIコンポーネントを使用する
- データはカード（MUI Card）で囲んで表示する
- カードにはシャドウ（elevation: 1）を付与する
- 角丸は 8px を基本とする
- ボタンは角丸の塗りつぶしスタイル（contained）を使用する`,
  notion: `---
inclusion: fileMatch
globs:
  - "src/**/*.ts"
  - "src/**/*.tsx"
---

# デザインガイドライン

## デザインの方向性

Notionのようなミニマルデザイン。
余白を多めにとり、タイポグラフィで情報の階層を表現する。
装飾は最小限にし、コンテンツそのものに集中できるUIにする。

## カラーテーマ

| 用途 | 色 |
|------|-----|
| 背景色（メイン） | #FFFFFF |
| 背景色（ホバー） | #F7F6F3 |
| メインカラー | #2EAADC |
| アクセントカラー | #EB5757 |
| テキスト色（主） | #37352F |
| テキスト色（副） | #9B9A97 |
| ボーダー色 | #E9E9E7 |
| エラー色 | #EB5757 |
| 成功色 | #4DAB9A |

## ナビゲーション構造

- ヘッダー: 極薄のボーダーのみで区切り。アプリ名を左寄せ、フォントは軽め
- サイドバー: なし
- メインエリア: 中央寄せ（max-width: 720px）、余白を広めにとる
- 画面遷移: React Routerで切り替え

## レスポンシブ対応

PC表示のみを対象とする。モバイル対応は行わない。

## その他の方針

- MUIコンポーネントを使用するが、シャドウやボーダーは極力排除する
- フォントサイズの大小で情報の階層を表現する
- アイコンは控えめに使用する
- ボタンはアウトラインスタイル（outlined）またはテキストスタイル（text）を優先
- 角丸は 4px を基本とし、丸すぎないシャープな印象にする
- 行間を広めにとる（line-height: 1.7 以上）`,
  slack: `---
inclusion: fileMatch
globs:
  - "src/**/*.ts"
  - "src/**/*.tsx"
---

# デザインガイドライン

## デザインの方向性

Slackのようなダークパープルのアクセントとカラフルな配色。
サイドバーを活用したナビゲーション構造で、メインエリアは白基調にする。

## カラーテーマ

| 用途 | 色 |
|------|-----|
| 背景色（メイン） | #FFFFFF |
| 背景色（サイドバー） | #3F0E40 |
| メインカラー | #611F69 |
| アクセントカラー | #36C5F0 |
| テキスト色（主） | #1D1C1D |
| テキスト色（副） | #616061 |
| テキスト色（サイドバー） | #FFFFFF |
| ボーダー色 | #DDDDDD |
| エラー色 | #E01E5A |
| 成功色 | #2EB67D |

## ナビゲーション構造

- ヘッダー: なし（サイドバーにナビゲーションを集約）
- サイドバー: 左側に固定。ダークパープル背景。幅 240px。アプリ名と画面リンクを配置
- メインエリア: サイドバーの右側に白背景でコンテンツを表示
- 画面遷移: React Routerで切り替え。サイドバーのリンクで遷移

## レスポンシブ対応

PC表示のみを対象とする。モバイル対応は行わない。

## その他の方針

- MUIコンポーネントを使用する
- サイドバーは MUI Drawer（permanent）で実装する
- アクションボタンにはカラフルな色を使い、視認性を高める
- 角丸は 6px を基本とする
- 通知やステータス表示にはカラフルなバッジを使用する`,
  github: `---
inclusion: fileMatch
globs:
  - "src/**/*.ts"
  - "src/**/*.tsx"
---

# デザインガイドライン

## デザインの方向性

GitHubのような白基調で落ち着いた配色。
グレーの濃淡で情報の階層を表現し、グリーンをアクセントとして使用する。
情報を整然と並べる、開発者向けの実用的なUI。

## カラーテーマ

| 用途 | 色 |
|------|-----|
| 背景色（メイン） | #FFFFFF |
| 背景色（セカンダリ） | #F6F8FA |
| メインカラー | #0969DA |
| アクセントカラー | #1A7F37 |
| テキスト色（主） | #1F2328 |
| テキスト色（副） | #656D76 |
| ボーダー色 | #D0D7DE |
| エラー色 | #CF222E |
| 成功色 | #1A7F37 |

## ナビゲーション構造

- ヘッダー: 濃いグレー（#24292F）背景にアプリ名を白文字で表示。固定
- サイドバー: なし
- メインエリア: 中央寄せ（max-width: 1012px）、上部にタブナビゲーションを配置
- 画面遷移: React Routerで切り替え。タブクリックで遷移

## レスポンシブ対応

PC表示のみを対象とする。モバイル対応は行わない。

## その他の方針

- MUIコンポーネントを使用する
- テーブル表示を積極的に活用する（MUI Table）
- ボーダー（1px solid #D0D7DE）で要素を区切る。シャドウは使わない
- 角丸は 6px を基本とする
- ボタンはアウトラインスタイルを基本とし、主要アクションのみ塗りつぶし（グリーン）
- ラベルやバッジで状態を色分け表示する`,
};

const HandsonImplementation: React.FC = () => {
  const [selectedPattern, setSelectedPattern] = useState('x');

  return (
    <HandsonArticleLayout
      title="フロントエンド編 — 画面実装"
      seriesName="フロントエンド編"
      tags={['Hands-on', 'Frontend', 'Implementation', 'React', 'MUI']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: '画面入出力定義書の作成',
        path: '/devdocs/handson/screen-io',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「フロントエンド編 — 画面入出力定義書の作成」が完了している',
          'バックエンドプロジェクト（team-todo）がDockerで起動できる状態にある',
        ]}
        completionCriteria={[
          '.kiro/steering/design_guideline.md が作成されている',
          '全画面のReactコンポーネントが生成されている',
          'npm run dev で全画面が表示・操作できる',
          'バックエンドAPIと接続し、データの登録・取得・更新・削除が動作する',
        ]}
      >
        design_guideline.mdでデザインの方向性を定義し、画面入出力定義書をもとにAIに画面を実装させる。バックエンドAPIと接続して動作するアプリケーションを完成させる。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="フロントエンド編 — 画面入出力定義書の作成"
        description="前の記事"
        path="/devdocs/handson/screen-io"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '画面実装の進め方' },
          { id: 'section-2', label: 'design_guideline.mdを作成する' },
          { id: 'section-3', label: '1画面目を実装する' },
          { id: 'section-4', label: '残りの画面を実装する' },
          { id: 'section-5', label: 'バックエンドAPI接続で動作確認' },
          { id: 'section-6', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1: 画面実装の進め方 */}
      <WorkflowSection title="画面実装の進め方" id="section-1" number={1}>
        <WorkflowSection title="全体の流れ" subNumber="1.1">
          <WorkflowText>画面実装は以下の4ステップで進めます。</WorkflowText>

          <WorkflowTable
            headers={['#', 'ステップ', 'やること']}
            rows={[
              [
                '1',
                'design_guideline.md を作成する',
                'デザインの方向性とナビゲーション構造を定義する',
              ],
              [
                '2',
                '画面入出力定義書の単位でAIに実装させる',
                '1画面ずつプロンプトで指示→生成→動作確認',
              ],
              ['3', '動作確認して修正する', 'npm run dev でブラウザ確認→問題があれば修正指示'],
              [
                '4',
                'バックエンドAPIと接続する',
                'バックエンドを起動してフロントから実際にAPI呼び出し',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="実装単位" subNumber="1.2">
          <WorkflowText>
            AIに画面を実装させる単位は「画面入出力定義書1ファイル = 1回のプロンプト」が基本です。
          </WorkflowText>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" color="text.secondary">
              画面入出力定義書のファイル数はあなたのrfpの内容によって異なります。一覧・登録・編集で画面を分けている人は3回以上のプロンプトが必要ですし、1画面に全機能を詰め込んでいる人は1回で済みます。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2: design_guideline.mdを作成する */}
      <WorkflowSection title="design_guideline.mdを作成する" id="section-2" number={2}>
        <WorkflowSection title="design_guideline.md とは" subNumber="2.1">
          <WorkflowText>
            design_guideline.mdは「アプリ全体のデザインの方向性」をAIに伝えるSteeringファイルです。色合い・雰囲気・ナビゲーション構造を定義しておくことで、AIが画面を実装するとき一貫したデザインで生成してくれます。
          </WorkflowText>

          <WorkflowTipBox title="なぜdesign_guideline.mdが必要なのか">
            <WorkflowText>
              design_guideline.mdがないと、AIは画面ごとに異なるデザイン判断をします。一覧画面は青基調、登録画面は緑基調、といったちぐはぐなUIになりかねません。
            </WorkflowText>
            <WorkflowText>
              design_guideline.mdで「全体の方向性」を1箇所に定義しておくと、どの画面を実装するときも同じデザインルールが適用されます。
            </WorkflowText>
            <WorkflowText>
              読み込みモードは fileMatch（src/**/*.ts,
              src/**/*.tsx）に設定するため、ソースコードを編集するたびに自動でコンテキストに含まれます。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="テンプレート" subNumber="2.2">
          <WorkflowText>design_guideline.mdに書くべき項目は以下の通りです。</WorkflowText>

          <WorkflowTable
            headers={['項目', '内容', '例']}
            rows={[
              [
                'デザインの方向性',
                '全体の雰囲気を1〜2文で',
                '「Notionのようなミニマルで余白を活かしたデザイン」',
              ],
              [
                'カラーテーマ',
                'メインカラー / アクセントカラー / 背景色',
                '白基調、アクセントはブルー',
              ],
              [
                'ナビゲーション構造',
                'ヘッダー / サイドバー / タブ等の構成',
                'ヘッダー固定、左サイドバーなし',
              ],
              ['画面遷移', '画面間の遷移方法', 'React Routerで画面切り替え'],
              ['レスポンシブ対応', 'モバイル対応の有無', 'PC表示のみ（モバイル対応なし）'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="サンプル5パターン" subNumber="2.3">
          <WorkflowText>
            「デザインの方向性」をどう書けばよいか迷う方のために、実在するサービスを参考にした5パターンの完成例を用意しました。気に入ったものをそのまま使うか、アレンジしてください。
          </WorkflowText>

          <Stack spacing={1.5}>
            {DESIGN_PATTERNS.map((pattern) => (
              <ButtonBase
                key={pattern.id}
                onClick={() => setSelectedPattern(pattern.id)}
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 2,
                  borderColor: selectedPattern === pattern.id ? pattern.color : 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: pattern.color },
                }}
              >
                <Box sx={{ height: 6, bgcolor: pattern.color }} />
                <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: pattern.bgcolor,
                      border: 1,
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                  />
                  <Stack spacing={0.25} sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {pattern.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pattern.description}
                    </Typography>
                  </Stack>
                  {selectedPattern === pattern.id && (
                    <Typography variant="caption" fontWeight={600} sx={{ color: pattern.color }}>
                      選択中
                    </Typography>
                  )}
                </Stack>
              </ButtonBase>
            ))}
          </Stack>

          <WorkflowLockedCodeBlock password="Rm6jVa" language="markdown" showPassword>
            {PATTERN_CODES[selectedPattern]}
          </WorkflowLockedCodeBlock>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              UI/UXの知識に自信がない方は、サービス名を書くだけで十分です。「Notionのようなデザインにしてください」とAIに伝えれば、AIがそのサービスのデザインテイストを汲み取って実装します。細かい色コードやピクセル指定は不要です。上記の完成例はあくまで「こう書くとこういう結果になる」のリファレンスです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="2.4">
          <WorkflowText>
            上記5パターンの完成例を参考に、.kiro/steering/design_guideline.md を作成してください。
          </WorkflowText>
          <WorkflowList
            items={[
              '気に入ったパターンをそのまま使ってOKです',
              'アレンジしたい場合は、完成例の色やナビゲーション構造を変更してください',
              '独自デザインで進めたい方は、完成例の構成（デザインの方向性 / カラーテーマ / ナビゲーション構造 / レスポンシブ対応 / その他の方針）を参考に自分で考えて書いてください',
            ]}
          />

          <WorkflowTipBox title="React経験者でMUIを使わない場合">
            <WorkflowText>
              MUIを使わず独自デザインで進める方は、design_guideline.mdに以下の情報を追加してください:
            </WorkflowText>
            <WorkflowList
              items={[
                '使用するCSSフレームワーク（Tailwind CSS、styled-components等）',
                'コンポーネントの詳細な見た目（角丸、シャドウ、ボーダー、余白のpx指定等）',
                'フォントファミリーの指定',
                'ダークモード対応の有無',
                'ブレイクポイントの定義（レスポンシブ対応する場合）',
              ]}
            />
            <WorkflowText>
              MUIを使う場合はこれらの指定は不要です。MUIのデフォルトテーマが適用されるため、design_guideline.mdには方向性とカラーテーマだけ書けば十分です。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3: 1画面目を実装する */}
      <WorkflowSection title="1画面目を実装する" id="section-3" number={3}>
        <WorkflowText>
          ここでは1画面の実装を具体的な手順で解説します。最もシンプルな画面（一覧画面を推奨）から始めましょう。
        </WorkflowText>

        <WorkflowSection title="プロンプトで実装を指示する" subNumber="3.1">
          <WorkflowChatBubble role="human">
            {`docs/screens/SCR001_TODO一覧.md の画面入出力定義書に従って、
TODO一覧画面を実装してください。

補足:
- structure.md のディレクトリ構成に従ってファイルを配置してください
- ルーティング設定（src/routes/）も合わせて作成してください
- APIとの通信部分はモックデータで仮実装してください（後でバックエンド接続に切り替えます）
- App.tsx にルーティングを組み込んでください`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              最初は「モックデータで仮実装」を指示します。バックエンドとの接続はセクション5で行います。まずは画面が正しく表示・操作できることを確認することに集中しましょう。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="動作確認" subNumber="3.2">
          <WorkflowCodeBlock language="bash" code={`npm run dev`} />

          <WorkflowText>ブラウザで画面を開き、以下を確認します:</WorkflowText>
          <WorkflowList
            items={[
              '画面が表示されるか（白い画面やエラーが出ていないか）',
              '画面項目が画面入出力定義書と一致しているか',
              'ボタンやリンクのアクションが動作するか（画面遷移、ダイアログ表示等）',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="修正指示を出す" subNumber="3.3">
          <WorkflowText>動作確認で問題が見つかった場合、AIに修正を指示します。</WorkflowText>

          <WorkflowChatBubble role="human">
            {`TODO一覧画面に以下の修正を加えてください。

- テーブルのヘッダーが表示されていないので追加してください
- 削除ボタンをクリックしたとき、確認ダイアログを表示してから削除するようにしてください
- 一覧が空のとき「データがありません」というメッセージを表示してください`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              修正指示は具体的に書いてください。「見た目がおかしい」「動かない」だけでは、AIが何を直せばよいか分かりません。「何が」「どう」おかしいのか、「どうなってほしいのか」を明記しましょう。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="1画面完成の判断基準" subNumber="3.4">
          <WorkflowText>以下を全て満たしたら、その画面は完成です:</WorkflowText>
          <WorkflowList
            items={[
              '画面入出力定義書の全項目が画面に表示されている',
              '定義書のアクションが全て動作する（ボタンクリック→API呼び出し or 画面遷移）',
              'エラー状態（データなし、バリデーションエラー等）の表示が実装されている',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: 残りの画面を実装する */}
      <WorkflowSection title="残りの画面を実装する" id="section-4" number={4}>
        <WorkflowText>
          1画面目と同じ手順を、残りの画面入出力定義書に対して繰り返します。
        </WorkflowText>

        <WorkflowSection title="繰り返しの手順" subNumber="4.1">
          <WorkflowText>画面ごとに以下を行います:</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              'プロンプトで画面入出力定義書を指定して実装を指示する',
              'npm run dev で動作確認する',
              '問題があれば修正指示を出す',
              '完成判断基準（セクション3.4）を満たしたら次の画面へ',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="画面間の連携" subNumber="4.2">
          <WorkflowText>
            2画面目以降を実装する際は、画面間の遷移が正しく動作するか確認してください。
          </WorkflowText>

          <WorkflowChatBubble role="human">
            {`docs/screens/SCR002_TODO登録.md の画面入出力定義書に従って、
TODO登録画面を実装してください。

補足:
- structure.md のディレクトリ構成に従ってファイルを配置してください
- APIとの通信部分はモックデータで仮実装してください`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              ポイント
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              画面間の遷移（「一覧→登録」「登録完了→一覧に戻る」等）は画面入出力定義書のアクションセクションに定義済みです。AIが定義書を読んで自動的に実装するため、プロンプトで改めて指示する必要はありません。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: バックエンドAPI接続で動作確認 */}
      <WorkflowSection title="バックエンドAPI接続で動作確認" id="section-5" number={5}>
        <WorkflowText>
          全画面がモックデータで動作することを確認できたら、いよいよバックエンドAPIと接続します。
        </WorkflowText>

        <WorkflowSection title="バックエンドを起動する" subNumber="5.1">
          <WorkflowText>バックエンドプロジェクトのディレクトリで以下を実行します。</WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            ターミナル1: Docker（MySQL + Flyway）を起動
          </Typography>
          <WorkflowCodeBlock language="bash" code={`docker compose up -d`} />

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            ターミナル2: Spring Bootアプリケーションを起動
          </Typography>
          <WorkflowCodeBlock language="bash" code={`./gradlew bootRun`} />

          <WorkflowText>
            バックエンドが http://localhost:8080 で起動していることを確認します。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="モックをAPI接続に切り替える" subNumber="5.2">
          <WorkflowText>
            Kiroのチャットで、モックデータをバックエンドAPI接続に切り替えます。
          </WorkflowText>

          <WorkflowChatBubble role="human">
            {`現在モックデータで仮実装している部分を、バックエンドAPIへの実際の通信に切り替えてください。

APIのベースURL: http://localhost:8080
API仕様: docs/api_specs.yaml を参照してください。

全画面のAPI通信を一括で切り替えてください。
TanStack Query を使用し、src/api/ 配下にAPIクライアントとクエリ定義を作成してください。`}
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="動作確認" subNumber="5.3">
          <WorkflowText>
            フロントエンドの開発サーバーが起動した状態で、ブラウザから操作します。確認ポイント:
          </WorkflowText>
          <WorkflowList
            items={[
              'TODO一覧画面にバックエンドのデータが表示されるか',
              '新規登録すると一覧に反映されるか',
              '更新・削除が正しく動作するか',
              'エラー時（バリデーション違反等）に画面にメッセージが表示されるか',
            ]}
          />

          <WorkflowInfoBox type="important">
            <Typography variant="body2">
              <strong>想定される結果</strong>: 初回のAPI接続時にCORS（Cross-Origin Resource
              Sharing）エラーが発生する可能性があります。ブラウザの開発者ツール（F12）のコンソールに「Access-Control-Allow-Origin」関連のエラーが出ている場合は、バックエンド側にCORS設定を追加する必要があります。AIに「CORSエラーが出ているので解決してください」と伝えれば対応してくれます。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowInfoBox type="success">
            <Typography variant="body2" fontWeight={600}>
              成功！
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              フロントエンドからバックエンドAPIを呼び出してデータの操作ができたら、アプリケーションの完成です。要件定義から始めて、バックエンドAPI、フロントエンド画面まで全てをAI駆動で開発しました。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: 現在のプロジェクト構成 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-6" number={6}>
        <WorkflowInfoBox type="info">
          <Typography variant="body2" color="text.secondary">
            src/pages/ や src/components/
            配下のファイルは、あなたの画面設計によって異なります。以下はあくまで一例です。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowDirectoryTree
          root="team_todo_front/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'tech.md' },
                    { name: 'structure.md' },
                    { name: 'api_specs_rule.md' },
                    { name: 'screen_io_rule.md' },
                    { name: 'design_guideline.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            {
              name: 'docs',
              children: [
                { name: 'api_specs.yaml' },
                {
                  name: 'screens',
                  children: [{ name: 'SCR001_TODO一覧.md' }, { name: 'SCR002_TODO登録.md' }],
                },
              ],
            },
            {
              name: 'node_modules',
              defaultOpen: false,
            },
            {
              name: 'public',
              defaultOpen: false,
            },
            {
              name: 'src',
              color: '#388e3c',
              children: [
                {
                  name: 'api',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [
                    { name: 'client.ts' },
                    {
                      name: 'queries',
                      children: [{ name: 'todoQueries.ts' }],
                    },
                  ],
                },
                {
                  name: 'components',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [
                    {
                      name: 'common',
                      children: [{ name: 'ConfirmDialog.tsx' }],
                    },
                  ],
                },
                {
                  name: 'hooks',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [{ name: 'useTodos.ts' }],
                },
                {
                  name: 'pages',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [{ name: 'TodoListPage.tsx' }, { name: 'TodoCreatePage.tsx' }],
                },
                {
                  name: 'routes',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [{ name: 'index.tsx' }],
                },
                {
                  name: 'types',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [{ name: 'todo.ts' }],
                },
                { name: 'App.tsx', desc: '今回修正', highlight: '#e65100' },
                { name: 'main.tsx' },
              ],
            },
            { name: 'HELPME.md' },
            { name: 'index.html' },
            { name: 'package.json' },
            { name: 'tsconfig.json' },
            { name: 'vite.config.ts' },
          ]}
        />
      </WorkflowSection>

      {/* ハンズオン完了メッセージ */}
      <WorkflowInfoBox type="success">
        <Typography variant="body2" fontWeight={600}>
          フロントエンド編 完了！
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
          要件定義 → バックエンドAPI実装 → フロントエンド画面実装
          まで、AI駆動開発で一気通貫のアプリケーション開発を体験しました。
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
          振り返ると、あなたが手を動かしたのは:
        </Typography>
        <WorkflowList
          items={[
            'rfp.md（要件を言語化する）',
            'Steeringルール（AIへの指示を定義する）',
            'チェックリスト（品質を判断する）',
            '修正指示（AIに具体的に伝える）',
          ]}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
          コード自体はほぼ全てAIが書いています。「何を作るか」を考え、「正しく作れたか」を判断する —
          それが AI駆動開発におけるエンジニアの仕事です。
        </Typography>
      </WorkflowInfoBox>

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/steering/design_guideline.md を作成した',
              '全画面のReactコンポーネントが生成されている',
              'npm run dev で全画面が表示・操作できる',
              '画面間の遷移（リンク・ボタン）が正しく動作する',
              'バックエンドを起動し、フロントからAPI接続できている',
              'データの登録・取得・更新・削除がブラウザから操作できる',
              'エラー時（バリデーション違反等）にメッセージが画面に表示される',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonImplementation;
